import React, { useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { getTodayISO } from '../../utils/dateUtils';

// 予約番号生成関数（i, l, o, 0, 1を除いた英数字4桁）
const generateReservationCode = () => {
  const chars = 'abcdefghjkmnpqrstuvwxyz23456789';
  let code = '';
  for (let i = 0; i < 4; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

// 必要なテーブル数を計算（大人5名以上で2卓必要）
const getRequiredTableCount = (adultCount) => {
  return adultCount >= 5 ? 2 : 1;
};

function AddReservationModal({ onClose, onAdd }) {
  const [formData, setFormData] = useState({
    reservation_date: getTodayISO(),
    reservation_time: '17:00',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    adult_count: 1,
    child_count: 0,
    seat_type: 'counter',
    status: 'confirmed',
    notes: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name.trim()) {
      alert('お名前を入力してください。');
      return;
    }
    if (!formData.customer_phone.trim()) {
      alert('電話番号を入力してください。');
      return;
    }
    if (parseInt(formData.adult_count) < 1) {
      alert('大人の人数は1名以上必要です。');
      return;
    }

    setLoading(true);

    try {
      const reservationCode = generateReservationCode();
      
      // テーブル席の場合、必要な卓数を計算
      const adultCount = parseInt(formData.adult_count);
      const tableCount = formData.seat_type === 'table' ? getRequiredTableCount(adultCount) : null;

      const newReservation = {
        reservation_date: formData.reservation_date,
        reservation_time: formData.reservation_time,
        customer_name: formData.customer_name.trim(),
        customer_phone: formData.customer_phone.trim(),
        customer_email: formData.customer_email.trim() || null,
        adult_count: adultCount,
        child_count: parseInt(formData.child_count),
        seat_type: formData.seat_type,
        status: formData.status,
        notes: formData.notes.trim() || null,
        reservation_code: reservationCode,
      };

      // table_countカラムが存在する場合のみ追加
      if (tableCount !== null) {
        newReservation.table_count = tableCount;
      }

      const { error } = await supabaseClient
        .from('reservations')
        .insert(newReservation);

      if (error) {
        // table_countカラムが存在しない場合のエラーを特別に処理
        if (error.code === 'PGRST204' && error.message.includes('table_count')) {
          // table_countを除いて再試行
          delete newReservation.table_count;
          const { error: retryError } = await supabaseClient
            .from('reservations')
            .insert(newReservation);
          if (retryError) throw retryError;
        } else {
          throw error;
        }
      }

      alert(`予約を追加しました。\n予約番号: ${reservationCode}`);
      onAdd();
      onClose();
    } catch (error) {
      console.error('予約追加エラー:', error);
      alert('予約の追加に失敗しました。\n' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 時間選択オプションを生成（11:00〜21:00まで30分刻み）
  const timeOptions = [];
  for (let hour = 11; hour <= 21; hour++) {
    timeOptions.push(`${String(hour).padStart(2, '0')}:00`);
    if (hour < 21 || (hour === 21 && true)) {
      timeOptions.push(`${String(hour).padStart(2, '0')}:30`);
    }
  }

  return (
    <div className="modal active" id="addModal">
      <div className="modal-content">
        <span className="close-modal" onClick={onClose}>
          &times;
        </span>
        <h2>新規予約追加</h2>
        <form id="addReservationForm" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>予約日 *</label>
            <input
              type="date"
              value={formData.reservation_date}
              onChange={(e) =>
                setFormData({ ...formData, reservation_date: e.target.value })
              }
              required
            />
          </div>
          <div className="form-group">
            <label>予約時間 *</label>
            <select
              value={formData.reservation_time}
              onChange={(e) =>
                setFormData({ ...formData, reservation_time: e.target.value })
              }
              required
            >
              {timeOptions.map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>お名前（カタカナ） *</label>
            <input
              type="text"
              value={formData.customer_name}
              onChange={(e) =>
                setFormData({ ...formData, customer_name: e.target.value })
              }
              placeholder="ヤマダ　タロウ"
              required
            />
          </div>
          <div className="form-group">
            <label>電話番号 *</label>
            <input
              type="tel"
              value={formData.customer_phone}
              onChange={(e) =>
                setFormData({ ...formData, customer_phone: e.target.value })
              }
              placeholder="09012345678"
              required
            />
          </div>
          <div className="form-group">
            <label>メールアドレス</label>
            <input
              type="email"
              value={formData.customer_email}
              onChange={(e) =>
                setFormData({ ...formData, customer_email: e.target.value })
              }
              placeholder="example@email.com"
            />
          </div>
          <div className="form-group">
            <label>大人の人数 *</label>
            <input
              type="number"
              min="1"
              max="20"
              value={formData.adult_count}
              onChange={(e) =>
                setFormData({ ...formData, adult_count: e.target.value })
              }
              required
            />
          </div>
          <div className="form-group">
            <label>子供の人数</label>
            <input
              type="number"
              min="0"
              max="10"
              value={formData.child_count}
              onChange={(e) =>
                setFormData({ ...formData, child_count: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>席種 *</label>
            <select
              value={formData.seat_type}
              onChange={(e) =>
                setFormData({ ...formData, seat_type: e.target.value })
              }
              required
            >
              <option value="counter">カウンター</option>
              <option value="table">テーブル{parseInt(formData.adult_count) >= 5 ? '（2卓）' : ''}</option>
            </select>
            {formData.seat_type === 'table' && parseInt(formData.adult_count) >= 5 && (
              <p style={{ color: '#ffa500', fontSize: '0.85rem', marginTop: '5px' }}>
                ※大人5名以上のため、テーブル2卓を使用します
              </p>
            )}
          </div>
          <div className="form-group">
            <label>ステータス</label>
            <select
              value={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value })
              }
            >
              <option value="confirmed">確定</option>
              <option value="pending">店舗確認中</option>
            </select>
          </div>
          <div className="form-group">
            <label>備考</label>
            <textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              placeholder="アレルギーや特別なリクエストなど"
              rows={3}
            />
          </div>
          <div className="modal-buttons">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? '追加中...' : '予約を追加'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
            >
              キャンセル
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddReservationModal;

